import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  Grid,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  useTheme,
  Stack,
} from '@mui/material';
import { RetailerDocumentModel } from '../../../../app/services/api/ofdClient';
import { Download, UploadFile, ExpandMore } from '@mui/icons-material';
import { Trash01 } from '../../../../assets';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import { useState, useMemo, useContext } from 'react';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import { Pill } from '../../../../components/shared/Pill';
import { UserContext } from '../../../../components/shared/useUser';

interface OnboardingDueDiligenceProps {
  formData: RetailerDocumentModel[] | undefined;
  deleteFile: Function;
  approve: Function;
  upload: Function;
  download: Function;
  setIsFormDirty: Function;
}

const OnboardingDueDiligence: React.FC<OnboardingDueDiligenceProps> = ({ formData, deleteFile, approve, upload, download, setIsFormDirty }) => {
  const { t } = useTranslation('onboardingWizard');
  const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<RetailerDocumentModel | undefined>(undefined);
  const theme = useTheme();
  const { hasPermissionTo } = useContext(UserContext);

  const groupedDocuments = useMemo(() => {
    if (!formData) return {};

    const grouped: Record<string, RetailerDocumentModel[]> = {};

    formData.forEach(doc => {
      const header = doc.header || 'Other Documents';
      if (!grouped[header]) {
        grouped[header] = [];
      }
      grouped[header].push(doc);
    });

    Object.keys(grouped).forEach(header => {
      grouped[header].sort((a, b) => (a.category || '').localeCompare(b.category || ''));
    });

    return grouped;
  }, [formData]);

  const handleFileInputChange = (category: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      upload(category, file);
      setIsFormDirty(true);
    }
  };

  const headerCompletionStats = useMemo(() => {
    const stats: Record<string, { approved: number; total: number }> = {};

    if (!formData) return stats;

    Object.keys(groupedDocuments).forEach(header => {
      const docs = groupedDocuments[header];
      const approvedCount = docs.filter(doc => doc.approved).length;

      stats[header] = {
        approved: approvedCount,
        total: docs.length,
      };
    });

    return stats;
  }, [formData, groupedDocuments]);

  const onOpenDeleteModal = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, doc: RetailerDocumentModel) => {
    event.stopPropagation();
    setDeleteModalOpen(true);
    setSelectedDocument(doc);
  };

  const onCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedDocument(undefined);
  };

  const handleDelete = () => {
    deleteFile(selectedDocument);
    setIsFormDirty(true);
    onCloseDeleteModal();
    setShowDeleteMessage(true);
  };

  return (
    <>
      <Box sx={{ p: 6 }}>
        {Object.keys(groupedDocuments).map((header, index) => {
          const stats = headerCompletionStats[header];
          return (
            <Accordion
              key={header}
              defaultExpanded={index === 0}
              sx={{
                mb: 3,
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                borderRadius: '8px',
                '&:before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  backgroundColor: theme.palette.custom.gray[50],
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  borderBottom: `1px solid ${theme.palette.custom.gray[200]}`,
                }}
              >
                <Stack direction="row"
                  spacing={2}
                  alignItems="center">
                  <Typography sx={{ fontWeight: '600', ml: 2 }}>
                    {header}
                  </Typography>

                  {/* Add Pill component to show completion status */}
                  {stats && (
                    <Pill
                      text={`${stats.approved}/${stats.total} ${t('dueDiligenceTab.complete', 'Complete')}`}
                    />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Grid container
                  spacing={3}>
                  {groupedDocuments[header].map((doc, index) => (
                    <Grid
                      key={doc.category || index}
                      item
                      xs={12}
                      sm={6}
                      container
                      alignItems="center"
                      spacing={2}
                      sx={{
                        p: 1,
                        '&:hover': {
                          backgroundColor: theme.palette.custom.gray[50],
                          borderRadius: '4px',
                        },
                      }}
                    >
                      <Grid item
                        xs={1}>
                        <Tooltip
                          title={t('dueDiligenceTab.verify')}
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                '& .MuiTooltip-arrow': {
                                  color: theme.palette.primary.main,
                                },
                              },
                            },
                          }}
                        >
                          <span>
                            <Checkbox
                              disabled={
                                (doc.delete) ||
                                (doc.uploadRequired && !doc.fileExists) ||
                                !hasPermissionTo(['RetailersDetailsDocumentationVerify'])
                              }
                              checked={doc.approved || false}
                              onClick={() => { approve(doc.category!); setIsFormDirty(true); }}
                            />
                          </span>
                        </Tooltip>
                      </Grid>
                      <Grid item
                        xs={6}>
                        <Typography component="label">
                          {doc.category}
                          {doc.fieldRequired &&
                            <Typography component="span"
                              className="MuiFormLabel-asterisk"
                              sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                              &nbsp;*
                            </Typography>
                          }
                        </Typography>
                      </Grid>
                      {doc.uploadRequired &&
                        <Grid item
                          xs={5}
                          display="flex"
                          gap={1}>
                          <input aria-hidden
                            type="file"
                            id={`file-upload-${doc.category}`}
                            disabled={!hasPermissionTo(['RetailersDetailsDocumentationUpload'])}
                            hidden
                            onChange={(e) => handleFileInputChange(doc.category!, e)} />
                          <IconButton
                            aria-label='upload'
                            color="primary"
                            disabled={!hasPermissionTo(['RetailersDetailsDocumentationUpload']) || doc.approved}
                            onClick={() => document.getElementById(`file-upload-${doc.category}`)?.click()}
                          >
                            <UploadFile />
                          </IconButton>

                          <IconButton color="primary"
                            aria-label='download'
                            onClick={() => download(doc.documentId)}
                            disabled={doc.delete || !doc.fileExists || doc.fileData != null || !hasPermissionTo(['RetailersDetailsDocumentationDownload'])}>
                            <Download />
                          </IconButton>

                          <IconButton
                            aria-label='delete'
                            sx={{ color: theme.palette.error.main }}
                            onClick={(event) => onOpenDeleteModal(event, doc)}
                            disabled={doc.delete || doc.approved || (!doc.fileExists && doc.fileData == null) || !hasPermissionTo(['RetailersDetailsDocumentationDelete'])}>
                            <Trash01 />
                          </IconButton>
                        </Grid>
                      }
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
        <MessagesSnackbar open={showDeleteMessage}
          onClose={() => { setShowDeleteMessage(false); }}
          message={'Document deleted'}
          severity='success'
          duration={null} />
      </Box>

      <ConfirmationDialog
        open={deleteModalOpen}
        onCancel={() => onCloseDeleteModal()}
        onConfirm={() => handleDelete()}
        icon={<Trash01 />}
        title={t('dueDiligenceTab.deleteModal.confirm')}
        message={t('dueDiligenceTab.deleteModal.confirmMessage')}
        confirmButtonText={t('dueDiligenceTab.deleteModal.yes')}
        cancelButtonText={t('dueDiligenceTab.deleteModal.no')} />
    </>
  );
};

export default OnboardingDueDiligence;